package com.banyan.flightviewer.web.components.airport

import com.banyan.flightviewer.state.AirportFlight
import com.banyan.flightviewer.state.AirportInfo
import com.banyan.flightviewer.util.nowMs

/**
 * Tiny in-memory TTL cache for airport tab data. Keeps the user from
 * hammering the 250-req/day FR24 budget when clicking between a small set
 * of airports -- a 5-minute TTL is long enough to cover the typical "look
 * at three airports in the same sitting" flow, short enough that schedule
 * changes show up on revisit.
 *
 * Holds three cache maps (info, departures, arrivals), keyed by the
 * uppercased airport code. Each entry is timestamped with [nowMs()];
 * lookups return null if the entry is missing OR expired.
 */
object AirportCache {
    private const val TTL_MS: Long = 5 * 60 * 1000L

    private data class Entry<T>(val data: T, val cachedAt: Long)

    private val info = mutableMapOf<String, Entry<AirportInfo?>>()
    private val deps = mutableMapOf<String, Entry<List<AirportFlight>>>()
    private val arrs = mutableMapOf<String, Entry<List<AirportFlight>>>()

    fun getInfo(code: String): AirportInfo? = info[code.uppercase()]
        ?.takeIf { nowMs() - it.cachedAt < TTL_MS }
        ?.data

    fun putInfo(code: String, value: AirportInfo?) {
        info[code.uppercase()] = Entry(value, nowMs())
    }

    fun getDepartures(code: String): List<AirportFlight>? = deps[code.uppercase()]
        ?.takeIf { nowMs() - it.cachedAt < TTL_MS }
        ?.data

    fun putDepartures(code: String, value: List<AirportFlight>) {
        deps[code.uppercase()] = Entry(value, nowMs())
    }

    fun getArrivals(code: String): List<AirportFlight>? = arrs[code.uppercase()]
        ?.takeIf { nowMs() - it.cachedAt < TTL_MS }
        ?.data

    fun putArrivals(code: String, value: List<AirportFlight>) {
        arrs[code.uppercase()] = Entry(value, nowMs())
    }
}
